/**
 * Safeguard against deferred router flush on direct URL load.
 *
 * Symptom (#9 deep-link): opening a URL with search params (`?day=`, `?habit=`)
 * in a new tab, TanStack Router may mount the route component a
 * microtask BEFORE applying/validating search params. If a `createEffect`
 * initializes a signal from `useSearch()` in that first tick, it reads `{}` and the
 * deep-link is lost silently (the state is not reconstructed).
 *
 * `readInitialSearchParam()` captures `location.search` SYNCHRONOUSLY on first
 * render (before router can overwrite it) and makes it available to
 * seed the signal. Subsequent reactivity is still handled by `useSearch()`;
 * this only bridges the first tick gap on direct load.
 *
 * Idempotent: parsing the same URL produces the same value, so reloading
 * reconstructs the exact same state.
 */
export function readInitialSearchParam(key: string): string | undefined {
  if (globalThis.location === undefined) {
    return undefined;
  }
  const value = new URLSearchParams(globalThis.location.search).get(key);
  return value !== null && value.trim() !== "" ? value : undefined;
}
